// src/components/PropertyPanel/CommonProperties/StyleControl.js
import React, { useState, useEffect } from 'react';
import { usePropertyPanel } from '../PropertyPanelContext';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';

const StyleControl = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  const { handleStyleChange, handleComponentPropsChange } = usePropertyPanel();
  const style = selectedComponent?.style || {};
  
  // 클래스명 태그 관련 상태
  const [classNames, setClassNames] = useState([]);
  const [classNameInput, setClassNameInput] = useState('');
  
  // inline CSS 관련 상태
  const [inlineCss, setInlineCss] = useState('');
  
  // 스타일 객체를 CSS 문자열로 변환하는 함수
  const styleObjectToCssString = (styleObj) => {
    if (!styleObj || typeof styleObj !== 'object') return '';
    
    const cssLines = [];
    const skipProperties = ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'borderRadius'];
    
    Object.entries(styleObj).forEach(([property, value]) => {
      if (value && value !== '' && !skipProperties.includes(property)) {
        // camelCase를 kebab-case로 변환
        const kebabProperty = property.replace(/([A-Z])/g, '-$1').toLowerCase();
        cssLines.push(`${kebabProperty}: ${value}`);
      }
    });
    
    return cssLines.join('; ');
  };

  // 현재 적용된 CSS를 가져오는 함수
  const getCurrentCss = () => {
    if (!selectedComponent?.style) return '';
    return styleObjectToCssString(selectedComponent.style);
  };


  // 선택된 컴포넌트가 변경될 때 클래스명 목록과 CSS 업데이트
  useEffect(() => {
    if (selectedComponent?.componentProps?.className) {
      const existingClasses = Array.isArray(selectedComponent.componentProps.className) 
        ? selectedComponent.componentProps.className 
        : selectedComponent.componentProps.className.split(' ').filter(cls => cls.trim());
      setClassNames(existingClasses);
      

    } else {
      setClassNames([]);
    }
    
    // 현재 적용된 CSS를 textarea에 로드
    const currentCss = getCurrentCss();
    setInlineCss(currentCss);
  }, [selectedComponent]);
  
  // 박스 섀도우 설정
  const handleBoxShadowChange = (isChecked) => {
    if (!selectedComponent) return;
    
    if (isChecked) {
      handleStyleChange(selectedComponent, 'boxShadow', '0 4px 6px rgba(0, 0, 0, 0.1)');
    } else {
      handleStyleChange(selectedComponent, 'boxShadow', 'none');
    }
  };
  
  // 클래스명 추가 핸들러
  const handleAddClassName = (e) => {
    if (e.key === 'Enter' && classNameInput.trim()) {
      e.preventDefault();
      const newClassName = classNameInput.trim();
      
      // 중복 체크
      if (!classNames.includes(newClassName)) {
        const updatedClasses = [...classNames, newClassName];
        setClassNames(updatedClasses);
        handleComponentPropsChange('className', updatedClasses.join(' '));
        

      }
      
      setClassNameInput('');
    }
  };
  
  // 클래스명 삭제 핸들러
  const handleRemoveClassName = (classToRemove) => {
    const updatedClasses = classNames.filter(cls => cls !== classToRemove);
    setClassNames(updatedClasses);
    handleComponentPropsChange('className', updatedClasses.join(' '));
  };
  
  // CSS 파싱 유틸리티 함수
  const parseCssString = (cssString) => {
    const styles = {};
    if (!cssString.trim()) return styles;
    
    const declarations = cssString.split(';').filter(decl => decl.trim());
    
    declarations.forEach(declaration => {
      const colonIndex = declaration.indexOf(':');
      if (colonIndex > 0) {
        const property = declaration.substring(0, colonIndex).trim();
        const value = declaration.substring(colonIndex + 1).trim();
        
        if (property && value) {
          // CSS 속성명을 camelCase로 변환
          const camelProperty = property.replace(/-([a-z])/g, (match, letter) => 
            letter.toUpperCase()
          );
          styles[camelProperty] = value;
        }
      }
    });
    
    return styles;
  };
  
  // inline CSS 변경 핸들러 (실시간 적용)
  const handleInlineCssChange = (cssText) => {
    setInlineCss(cssText);
    
    if (!selectedComponent) return;
    
    const parsedStyles = parseCssString(cssText);  
    
    // 기존 스타일에서 패딩, 테두리 둥글기 등 기본 속성 유지
    const currentStyle = selectedComponent.style || {};
    const preserveProperties = ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'borderRadius'];
    const preservedStyles = {};
    
    preserveProperties.forEach(prop => {
      if (currentStyle[prop]) {
        preservedStyles[prop] = currentStyle[prop];
      }
    });
    
    // 새로운 스타일 객체 생성 (기본 속성 + 파싱된 스타일)
    const newStyle = {
      ...preservedStyles,
      ...parsedStyles
    };
    
    // 전체 스타일 객체를 한번에 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        style: newStyle
      }
    }));
  };
  
  // 모든 inline 스타일 제거 핸들러
  const handleClearInlineStyles = () => {
    if (!selectedComponent) return;
    
    // 현재 스타일 객체의 모든 속성을 빈 문자열로 설정하여 제거
    const currentStyle = selectedComponent.style || {};
    const clearedStyle = {};
    
    // 기본 레이아웃 속성들은 유지하고 나머지는 제거
    const preserveProperties = ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'borderRadius'];
    
    Object.keys(currentStyle).forEach(property => {
      if (preserveProperties.includes(property)) {
        clearedStyle[property] = currentStyle[property];
      } else {
        clearedStyle[property] = '';
      }
    });
    
    // 전체 스타일 객체를 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        style: clearedStyle
      }
    }));
    
    // textarea도 빈 값으로 설정
    setInlineCss('');
    

  };
  
  return (
    <div className="property-group">
      <div className="property-group-title">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 17L9 11L13 15L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M17 7H21V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        스타일 설정
      </div>
      <div className="property-controls">
        <div className="property-control">
          <label>배경색</label>
          <div className="color-picker-container">
            <input 
              type="color" 
              value={style?.backgroundColor || '#ffffff'} 
              onChange={(e) => handleStyleChange(selectedComponent, 'backgroundColor', e.target.value)}
            />
          </div>
        </div>
        <div className="property-control">
          <label>테두리 색상</label>
          <div className="color-picker-container">
            <input 
              type="color" 
              value={style?.borderColor || '#cccccc'} 
              onChange={(e) => handleStyleChange(selectedComponent, 'borderColor', e.target.value)}
            />
          </div>
        </div>
        <div className="property-control">
          <label>테두리 두께</label>
          <input 
            type="number" 
            min="0" 
            max="10" 
            value={parseInt(style?.borderWidth) || 0} 
            onChange={(e) => handleStyleChange(selectedComponent, 'borderWidth', `${e.target.value}px`)}
          />
        </div>
        <div className="property-control">
          <label>그림자 효과</label>
          <input 
            type="checkbox"
            checked={style?.boxShadow && style.boxShadow !== 'none'} 
            onChange={(e) => handleBoxShadowChange(e.target.checked)}
          />
        </div>
        
        {/* 클래스명 태그 입력 */}
        <div className="property-control">
          <label>클래스명</label>
          <div className="class-name-input-container">
            <input
              type="text"
              value={classNameInput}
              onChange={(e) => setClassNameInput(e.target.value)}
              onKeyDown={handleAddClassName}
              placeholder="클래스명을 입력하고 엔터를 누르세요"
              className="class-name-input"
            />
            <div className="class-name-tags">
              {classNames.map((className, index) => (
                <span key={index} className="class-name-tag">
                  {className}
                  <button
                    type="button"
                    onClick={() => handleRemoveClassName(className)}
                    className="remove-tag-btn"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
        
        {/* Inline CSS 입력 */}
        <div className="property-control">
          <label>Inline CSS</label>
          <div className="inline-css-container">
            <textarea
              value={inlineCss}
              onChange={(e) => handleInlineCssChange(e.target.value)}
              placeholder="예: color: red; font-size: 16px; margin: 10px;&#10;&#10;현재 적용된 스타일이 표시되며, 수정하면 실시간으로 적용됩니다."
              className="inline-css-input"
              rows="4"
            />
            {inlineCss && (
              <button
                type="button"
                onClick={handleClearInlineStyles}
                className="clear-css-btn"
                title="모든 인라인 스타일을 제거합니다 (패딩, 테두리 둥글기 제외)"
              >
                스타일 초기화
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StyleControl;